import { getUserFetchInfo } from '@api/fetch-support/client';
import { LocalStorage } from '@mentimeter/storage';
import type { TrackingPayload } from './payload';
import { getTrackingURL } from '@api/fetch-support';
import { getDevelopmentFlag } from '@mentimeter/development-flags';
import { getLocalStorageVisitorToken } from '@mentimeter/visitor/visitor-token';

// To not have a dependency on http-clients
interface UserTokenLocalStorage {
  token: string;
}

export function trackUser(payload: TrackingPayload) {
  const { region } = getUserFetchInfo() ?? { region: null };
  const userTrackingToken =
    LocalStorage.getJSONItem<UserTokenLocalStorage>('user')?.token;
  if (!userTrackingToken || !region) {
    logOnTest(`No user tracking token or region found. Skipping tracking.`);
    return;
  }

  const trackingUrl = getTrackingURL(region);

  const encodedData = toBase64String(payload);
  const trackURL = `${trackingUrl}/event/user?data=${encodedData}&token=${userTrackingToken}`;

  const config: RequestInit = {
    method: 'POST',
  };

  logOnTest('tracking user:', payload);

  if (isBlabTrackingDisabled()) {
    return;
  }

  try {
    if ('sendBeacon' in navigator) {
      navigator.sendBeacon(trackURL);
    } else {
      // No await, here happens in 0,3% of cases as of July 2024, best effort fetch
      fetch(`${trackURL}&missingBeacon=true`, config);
    }
  } catch (err) {
    logOnTest(`Error sending tracking event: ${err}`);
    // Swallow tracking errors
  }
}

const VISITOR_TRACKING_REGION = 'eu';

export async function trackVisitor(payload: TrackingPayload) {
  const visitorToken = getLocalStorageVisitorToken();
  if (!visitorToken) {
    logOnTest(`No visitor tracking token or region found. Skipping tracking.`);
    return;
  }

  const encodedData = toBase64String(payload);
  const trackingUrl = getTrackingURL(VISITOR_TRACKING_REGION);
  const trackURL = `${trackingUrl}/event/visitor?data=${encodedData}&token=${visitorToken}`;

  logOnTest('visitor', payload);

  if (isBlabTrackingDisabled()) {
    return;
  }

  try {
    if ('sendBeacon' in navigator) {
      navigator.sendBeacon(trackURL);
    } else {
      await fetch(`${trackURL}&missingBeacon=true`, { method: 'POST' });
    }
  } catch (error) {
    // Swallow tracking errors
  }
}

function logOnTest(message: unknown, ...logParams: Array<unknown>) {
  try {
    if (
      localStorage.getItem('console-tracking') === 'true' ||
      getDevelopmentFlag('console-tracking') === 'on'
    ) {
      // eslint-disable-next-line
      console.log(`tracking "${message}":`, JSON.stringify(logParams, null, 4));
    }
  } catch (error) {
    // localStorage is prone to throwing errors, but we dont care about them:
    // this logging block is for testing only
  }
}

function isBlabTrackingDisabled(): boolean {
  try {
    if (
      localStorage.getItem('menti.disable-blab-tracking') === 'true' ||
      getDevelopmentFlag('disable-blab-tracking') === 'on'
    ) {
      return true;
    }
  } catch (error) {
    // localStorage is prone to throwing errors, but we dont care about them.
  }
  return false;
}

function toBase64String(data: Record<string, any>) {
  return Buffer.from(JSON.stringify(data)).toString('base64');
}
